import { useEffect, useState } from 'react';
import { Err } from '../ui.js';
import {
  listRecords, createRecord, updateRecord, deleteRecord, ApiError,
} from '../api.js';
import type { Offering } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// WHAT YOU SELL — the missing half of the ICP.
//
// The app has always known who to target and never what to offer them, so
// every drafted email had to imply the offer from the operator's title. That
// works for one user who is the author. It produces vague mail for anyone else.
//
// AN OFFERING IS FOUR SENTENCES, NOT A PRICE LIST. Name, the problem it
// removes, the outcome it produces, and proof it has worked. Those are the four
// things a cold email has to carry, which is why they are the four fields —
// this is a writing input, not a product catalogue.
//
// Price is optional and stays a BAND ("$8–15k/mo"), never a number. A precise
// figure in a CRM field ends up in a draft, and a fractional practice prices
// per engagement. The drafter is instructed never to invent one; this field is
// the only way a real price ever reaches a draft.
// ─────────────────────────────────────────────────────────────────────────────

const KINDS = ['retainer', 'project', 'hourly', 'advisory', 'product', 'service'];

const BLANK = {
  name: '', kind: 'retainer', problem: '', outcome: '',
  price_band: '', term: '', proof: [] as string[],
};

export function Offerings() {
  const [rows, setRows] = useState<Offering[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ ...BLANK });
  const [busy, setBusy] = useState(false);

  async function load() {
    try { setRows((await listRecords<Offering>('offerings')).records); }
    catch (e) { setError(e instanceof ApiError ? e.message : String(e)); }
  }
  useEffect(() => { void load(); }, []);

  function edit(o: Offering) {
    setEditing(o.id);
    setDraft({
      name: o.name, kind: o.kind, problem: o.problem ?? '', outcome: o.outcome ?? '',
      price_band: o.price_band ?? '', term: o.term ?? '', proof: o.proof ?? [],
    });
  }

  async function save() {
    if (!draft.name.trim()) return;
    setBusy(true); setError(null);
    try {
      const body = { ...draft, name: draft.name.trim() };
      if (editing && editing !== 'new') await updateRecord('offerings', editing, body);
      else await createRecord('offerings', { ...body, sort_order: rows?.length ?? 0 });
      setEditing(null); setDraft({ ...BLANK });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(false); }
  }

  async function remove(id: string) {
    setError(null);
    try { await deleteRecord('offerings', id); await load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : String(e)); }
  }

  return (
    <section className="panel">
      <h4>What you sell</h4>
      <p className="meta">
        Every email the app drafts is built from these. An offering with no problem
        and no outcome produces a draft that talks about you.
      </p>

      <Err>{error}</Err>

      {rows?.length === 0 && editing === null && (
        <p className="empty">
          Nothing yet — the drafter has no offer to make. Add the one you sell most.
        </p>
      )}

      {rows?.map((o) => (
        editing === o.id ? (
          <Form key={o.id} draft={draft} setDraft={setDraft} busy={busy}
            onSave={() => void save()} onCancel={() => setEditing(null)} />
        ) : (
          <div className="offering" key={o.id}>
            <div className="offeringhead">
              <strong>{o.name}</strong>
              <span className="flag flag-mute">{o.kind}</span>
              {o.price_band && <span className="meta">{o.price_band}</span>}
              {o.term && <span className="meta">· {o.term}</span>}
            </div>
            {/* The two lines that end up in a draft, shown as the summary —
                if they read badly here they will read badly in an email. */}
            {o.problem && <p className="offeringline"><em>Fixes:</em> {o.problem}</p>}
            {o.outcome && <p className="offeringline"><em>So that:</em> {o.outcome}</p>}
            {(o.proof ?? []).length > 0 && (
              <p className="offeringline"><em>Proof:</em> {(o.proof ?? []).join(' · ')}</p>
            )}
            <div className="offeringacts">
              <button className="linkish" onClick={() => edit(o)}>Edit</button>
              <button className="linkish danger" onClick={() => void remove(o.id)}>Remove</button>
            </div>
          </div>
        )
      ))}

      {editing === 'new' ? (
        <Form draft={draft} setDraft={setDraft} busy={busy}
          onSave={() => void save()} onCancel={() => setEditing(null)} />
      ) : (
        <button className="ghost" onClick={() => { setDraft({ ...BLANK }); setEditing('new'); }}>
          Add an offering
        </button>
      )}
    </section>
  );
}

function Form({ draft, setDraft, busy, onSave, onCancel }: {
  draft: typeof BLANK;
  setDraft: (d: typeof BLANK) => void;
  busy: boolean; onSave: () => void; onCancel: () => void;
}) {
  const set = (k: keyof typeof BLANK, v: string | string[]) =>
    setDraft({ ...draft, [k]: v });

  return (
    <div className="offeringform">
      <div className="pair">
        <label className="field">
          <span className="field-label">Name</span>
          <input value={draft.name} autoFocus placeholder="Fractional CMO retainer"
            onChange={(e) => set('name', e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Kind</span>
          <select value={draft.kind} onChange={(e) => set('kind', e.target.value)}>
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
      </div>

      <label className="field">
        <span className="field-label">The problem it removes</span>
        <input value={draft.problem}
          placeholder="Marketing is run by the founder between sales calls"
          onChange={(e) => set('problem', e.target.value)} />
      </label>

      <label className="field">
        <span className="field-label">The outcome it produces</span>
        <input value={draft.outcome}
          placeholder="A pipeline that does not depend on the founder's calendar"
          onChange={(e) => set('outcome', e.target.value)} />
      </label>

      <label className="field">
        <span className="field-label">Proof — one per line</span>
        <textarea rows={3} value={(draft.proof ?? []).join('\n')}
          placeholder="Took RimSys from founder-led to a $4M pipeline in 9 months"
          onChange={(e) => set('proof',
            e.target.value.split('\n').map((x) => x.trim()).filter(Boolean))} />
      </label>

      <div className="pair">
        <label className="field">
          <span className="field-label">Price band — optional</span>
          <input value={draft.price_band} placeholder="$8–15k / month"
            onChange={(e) => set('price_band', e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Term — optional</span>
          <input value={draft.term} placeholder="6 months, 3 days a week"
            onChange={(e) => set('term', e.target.value)} />
        </label>
      </div>
      {/* Said out loud, because a number typed here can reach a prospect. */}
      <p className="fieldhint">
        A band, never a number. The drafter is forbidden from inventing a price —
        whatever you type here is the only figure it can ever quote.
      </p>

      <div className="actions">
        <button className="primary" disabled={busy || !draft.name.trim()} onClick={onSave}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
